export const PRODUCTION_API_BASE = 'https://api.carbon-one.ca';

export const OCF_BASE_PATH = '/v1/ocf';

export const EXAMPLE_API_KEY = 'carbonone_sk_your_key_here';

export const NOT_PROVIDED_LABEL = 'Not provided';

export type RichListSegment =
  | { kind: 'plain'; text: string }
  | { kind: 'bold'; text: string }
  | { kind: 'italic'; text: string };

export type RichOrderedStep = {
  heading: string;
  segments: RichListSegment[];
};

export type ReferenceOverviewBlock = {
  id: string;
  navLabel: string;
  title: string;
  paragraphs: string[];
  codeBlocks?: string[];
  listItems?: string[];
  orderedListItems?: string[];
  /** Structured steps with inline emphasis (e.g. API key guide). When set, replaces orderedListItems for display. */
  richOrderedSteps?: RichOrderedStep[];
};

export type QueryParamRow = {
  name: string;
  type: string;
  required: boolean;
  description: string;
};

export type HeaderRow = {
  name: string;
  required: boolean;
  description: string;
};

export type FieldRow = {
  field: string;
  description: string;
};

export type ErrorRow = {
  status: string;
  situation: string;
  message: string;
};

export type ResponseBlock = {
  label: string;
  content: string;
};

export type ReferenceEndpointBlock = {
  id: string;
  title: string;
  navLabel: string;
  method: 'GET';
  path: string;
  description: string;
  queryParams: QueryParamRow[];
  queryParamsNote?: string;
  headers: HeaderRow[];
  curl: string;
  responses: ResponseBlock[];
  fields: FieldRow[];
  businessNotes: string[];
  errors: ErrorRow[];
};

export type ReferenceCategory = {
  id: string;
  navLabel: string;
  sectionIntro?: string[];
  endpoints: ReferenceEndpointBlock[];
};

export type QuickReferenceRow = {
  endpoint: string;
  allowed: string;
};

export type GlobalErrorExample = {
  id: string;
  status: '401' | '403' | '429' | '400' | '404';
  title: string;
  description: string;
  json: string;
};

const authHeaderRow: HeaderRow = {
  name: 'Authorization',
  required: true,
  description: 'Bearer <api_key>',
};

const buildCurl = (path: string, query: Array<[string, string]> = []): string => {
  const url = `${PRODUCTION_API_BASE}${path}`;
  if (!query.length) {
    return [
      `curl -sS -X GET "${url}" \\`,
      `  -H "Authorization: Bearer ${EXAMPLE_API_KEY}"`,
    ].join('\n');
  }
  const queryLines = query.map(([key, value]) => `  --data-urlencode "${key}=${value}" \\`);
  return [
    `curl -sS -G "${url}" \\`,
    ...queryLines,
    `  -H "Authorization: Bearer ${EXAMPLE_API_KEY}"`,
  ].join('\n');
};

export const overviewBlocks: ReferenceOverviewBlock[] = [
  {
    id: 'overview',
    navLabel: 'Overview',
    title: 'Overview',
    paragraphs: [
      'The CarbonOne External OCF API is a read-only HTTP API for accessing an organization\'s carbon data. All endpoints are mounted under the OCF base path and support GET only.',
      'All requests must use HTTPS and responses are always JSON.',
    ],
    codeBlocks: [`Base URL\n${PRODUCTION_API_BASE}`, `Base path\n${OCF_BASE_PATH}`],
  },
  {
    id: 'authentication',
    navLabel: 'Authentication',
    title: 'Authentication',
    paragraphs: [
      'Send your API key in a strict Authorization header.',
    ],
    listItems: [
      'Header must start with the exact prefix "Bearer " (including the trailing space).',
      'The token after "Bearer " must not be empty.',
    ],
    codeBlocks: [`Authorization: Bearer <your_api_key>`],
  },
  {
    id: 'rate-limits',
    navLabel: 'Rate limits',
    title: 'Rate limits',
    paragraphs: [
      'All /v1/* routes are protected by two Redis-backed rate limiters. Respect 429 responses and back off.',
    ],
    listItems: [
      'IP rate limit (failed requests): 30 per minute per IP.',
      'API key rate limit (successful authenticated requests): 100 per minute per API key (falls back to IP if needed).',
    ],
  },
  {
    id: 'global-conventions',
    navLabel: 'Global conventions',
    title: 'Global conventions',
    paragraphs: [
      'Numeric formatting rules apply to all response payloads:',
    ],
    listItems: [
      'Emission masses (total_co2e, etc.) are rounded to at most 4 decimal places.',
      'scope_emission[].percentage (share of total emissions by scope) uses 2 decimal places.',
      '*_by_source[].percentage (share within a single scope after OCF filtering) uses 2 decimal places.',
      'Each endpoint has a strict query parameter allowlist. Any other query parameter name returns 400 Bad Request.',
    ],
  },
  {
    id: 'create-api-key',
    navLabel: 'Create API key',
    title: 'How to Create an API Key',
    paragraphs: [],
    richOrderedSteps: [
      {
        heading: 'Log in to the platform',
        segments: [
          { kind: 'plain', text: 'Make sure you have ' },
          { kind: 'bold', text: 'admin access' },
          { kind: 'plain', text: '.' },
        ],
      },
      {
        heading: 'Open your profile menu',
        segments: [
          { kind: 'plain', text: 'Click on "' },
          { kind: 'bold', text: 'My Profile' },
          { kind: 'plain', text: '" from the left sidebar.' },
        ],
      },
      {
        heading: 'Go to Accounts',
        segments: [
          { kind: 'plain', text: 'Inside ' },
          { kind: 'italic', text: 'My Profile' },
          { kind: 'plain', text: ', select "' },
          { kind: 'bold', text: 'Accounts' },
          { kind: 'plain', text: '".' },
        ],
      },
      {
        heading: 'Navigate to API Keys tab',
        segments: [
          { kind: 'plain', text: 'In the Accounts page, click on the "' },
          { kind: 'bold', text: 'API Keys' },
          { kind: 'plain', text: '" section (next to ' },
          { kind: 'italic', text: 'User details' },
          { kind: 'plain', text: ' and ' },
          { kind: 'italic', text: 'Plans' },
          { kind: 'plain', text: ').' },
        ],
      },
      {
        heading: 'Create a new API key',
        segments: [
          { kind: 'plain', text: 'Click the "' },
          { kind: 'bold', text: '+ Create API Key' },
          { kind: 'plain', text: '" button on the right.' },
        ],
      },
      {
        heading: 'Copy and store the key securely',
        segments: [
          { kind: 'plain', text: 'The full API key will be shown ' },
          { kind: 'bold', text: 'only once' },
          { kind: 'plain', text: '. Make sure to save it in a secure location.' },
        ],
      },
    ],
  },
];

export const resourceCategories: ReferenceCategory[] = [
  {
    id: 'ocf-endpoints',
    navLabel: 'OCF',
    sectionIntro: [
      `All endpoints are mounted under ${PRODUCTION_API_BASE}${OCF_BASE_PATH} and only support GET.`,
    ],
    endpoints: [
      {
        id: 'ocf-list-organizations',
        title: 'List Organizations',
        navLabel: 'List Organizations',
        method: 'GET',
        path: `${OCF_BASE_PATH}/list-organizations`,
        description:
          'Returns every organization belonging to the authenticated company, with a boolean indicating which row is the parent (root) organization for the account.',
        queryParams: [],
        queryParamsNote: 'No query parameters are permitted. Any extra key returns 400 Bad Request.',
        headers: [authHeaderRow],
        curl: buildCurl(`${OCF_BASE_PATH}/list-organizations`),
        responses: [
          {
            label: 'Success (200)',
            content: `{
  "Organizations": [
    {
      "id": 101,
      "name": "Acme Parent",
      "Parent_Organization": true
    },
    {
      "id": 102,
      "name": "Acme Subsidiary",
      "Parent_Organization": false
    }
  ]
}`,
          },
        ],
        fields: [
          { field: 'Organizations', description: 'Array of organizations for the company.' },
          { field: 'Organizations[].id', description: 'Organization identifier.' },
          { field: 'Organizations[].name', description: 'Display name.' },
          {
            field: 'Organizations[].Parent_Organization',
            description:
              'true for the company\'s parent organization (from organization_year_wise.is_parent); false for all others.',
          },
        ],
        businessNotes: [
          'The parent organization is resolved via organization_year_wise where is_parent is true for the company.',
          'Ordering is by organization id ascending.',
        ],
        errors: [
          { status: '401', situation: 'Missing or invalid Authorization header', message: 'unauthorized' },
          { status: '403', situation: 'JWT used instead of API key', message: 'forbidden — JWT not accepted on /v1' },
          { status: '403', situation: 'API key without read scope', message: 'forbidden' },
          { status: '401', situation: 'Invalid or expired API key', message: 'Invalid or expired API key' },
          { status: '400', situation: 'Unknown query parameters', message: 'Invalid query parameter(s): ...' },
          { status: '429', situation: 'Rate limited', message: 'Too many requests, please try again later' },
        ],
      },
      {
        id: 'ocf-organization-summary',
        title: 'Organization Summary',
        navLabel: 'Organization Summary',
        method: 'GET',
        path: `${OCF_BASE_PATH}/organization-summary`,
        description:
          'Aggregated revenue, emissions, and location-level totals scoped to a chosen organization (defaults to the parent organization). Optionally filters by reporting year (defaults to the latest available year for the resolved organization).',
        queryParams: [
          {
            name: 'organization_id',
            type: 'integer',
            required: false,
            description:
              'Organization to summarize. If omitted, the parent organization for the company is used. Must belong to the authenticated company.',
          },
          {
            name: 'year',
            type: 'integer',
            required: false,
            description:
              'Reporting year. If omitted, the latest year available for the resolved organization is used.',
          },
        ],
        headers: [authHeaderRow],
        curl: buildCurl(`${OCF_BASE_PATH}/organization-summary`, [
          ['organization_id', '102'],
          ['year', '2024'],
        ]),
        responses: [
          {
            label: 'Success (200) — with data',
            content: `{
  "organization_id": 102,
  "organization_name": "Acme Subsidiary",
  "year": 2024,
  "data": {
    "revenue": {
      "revenue": 12500000.5,
      "currency": "CAD",
      "revenue_complete": true,
      "missing_revenue_locations": []
    },
    "emissions": {
      "yoy": {
        "yoy_emission": [
          { "year": 2022, "total_co2e": 1200.5 },
          { "year": 2023, "total_co2e": 1180.25 },
          { "year": 2024, "total_co2e": 1150.0 }
        ]
      },
      "by_source": {
        "scope1_by_source": [
          { "label": "Natural gas", "total_co2e": 100.1234, "percentage": 45.67 }
        ],
        "scope2_by_source": [
          { "label": "Electricity", "total_co2e": 200.5678, "percentage": 80.00 }
        ],
        "scope3_by_source": [
          { "label": "Category 1", "total_co2e": 300.9012, "percentage": 33.33 }
        ]
      },
      "by_scope": {
        "scope_emission": [
          { "scope": "Scope 1", "total_co2e": 100.1234, "percentage": 10.00 },
          { "scope": "Scope 2", "total_co2e": 200.5678, "percentage": 20.00 },
          { "scope": "Scope 3", "total_co2e": 700.9012, "percentage": 70.00 }
        ]
      }
    },
    "locations": {
      "emissions": {
        "location_organization_emissions": [
          {
            "id": 55,
            "name": "Plant A",
            "total_co2e": 400.1234,
            "entity_type": "Location"
          },
          {
            "id": null,
            "name": "Subsidiary Org X",
            "total_co2e": 250.5,
            "entity_type": "Organization"
          }
        ]
      }
    }
  }
}`,
          },
          {
            label: 'Success (200) — no data',
            content: `{
  "organization_id": 102,
  "organization_name": "Acme Subsidiary",
  "year": 2024,
  "message": "No data available for given parameters"
}`,
          },
        ],
        fields: [
          { field: 'year', description: 'Reporting year after resolution (top-level; not duplicated under data).' },
          {
            field: 'data.revenue.revenue',
            description: 'Total revenue in CAD for the organization (after internal org-share rollup).',
          },
          { field: 'data.revenue.currency', description: 'Always CAD for this endpoint.' },
          {
            field: 'data.revenue.revenue_complete',
            description:
              'true if every relevant activity_data_entry row for the period has revenue flagged complete; otherwise false.',
          },
          {
            field: 'data.revenue.missing_revenue_locations',
            description: 'When incomplete: list of { location_id, location_name } with missing revenue.',
          },
          {
            field: 'data.emissions.yoy.yoy_emission',
            description: 'Time series of total organization emissions by year (masses in t CO₂e, ≤ 4 dp).',
          },
          {
            field: 'data.emissions.by_scope.scope_emission',
            description: 'One row per Scope 1–3: total_co2e (t CO₂e), percentage of total emissions (2 dp).',
          },
          {
            field: 'data.emissions.by_source.scope*_by_source',
            description:
              'Per-scope breakdown by label; total_co2e is t CO₂e; percentage is within that scope only (after zero sources removed and percentages recomputed).',
          },
          {
            field: 'data.locations.emissions.location_organization_emissions',
            description:
              'Combined location and child organization rows, sorted by emissions; id may be null if the name could not be matched to a location id.',
          },
        ],
        businessNotes: [
          'Organization resolution: omit organization_id → parent organization for the company. Provide organization_id → must exist and company_id must match (tenant isolation).',
          'Year resolution: omit year → latest available year for the resolved organization. Provide year → must be in the available set or the API returns 404 listing available years.',
          'No-data response: if revenue and emissions are all effectively empty, the API returns a message and no data object.',
          'Location list cap: the underlying location/org emission list is paginated internally; large hierarchies may not return every row in one response.',
        ],
        errors: [
          { status: '400', situation: 'Unknown query parameters', message: 'Invalid query parameter(s): ...' },
          {
            status: '400',
            situation: 'Invalid organization_id / year (non-positive integer)',
            message: 'organization_id must be a positive integer / year must be a positive integer',
          },
          { status: '404', situation: 'Organization not in tenant', message: 'Organization not found' },
          { status: '404', situation: 'No reporting years', message: 'No reporting years available' },
          {
            status: '404',
            situation: 'Year not available',
            message: 'Year <y> is not available. Available years: ...',
          },
          { status: '401', situation: 'Auth / invalid API key', message: 'See global auth errors' },
          { status: '403', situation: 'Scope or JWT rejection', message: 'See global auth errors' },
          { status: '429', situation: 'Rate limited', message: 'Too many requests, please try again later' },
        ],
      },
      {
        id: 'ocf-location',
        title: 'Location Summary',
        navLabel: 'Location',
        method: 'GET',
        path: `${OCF_BASE_PATH}/location-summary`,
        description:
          'Revenue and emissions for a specific location when location_id is provided (scoped to that location\'s organization). When location_id is omitted, the API returns 200 with a message and a locations array so clients can choose a valid location_id (and optional year) before requesting a full payload. With location_id, responses use total_co2e and label in emissions breakdowns and currency on revenue, consistent with organization-summary.',
        queryParams: [
          {
            name: 'location_id',
            type: 'integer',
            required: false,
            description:
              'Location id; must belong to the company. If omitted, returns a selection helper (message + locations[]) instead of a full data block.',
          },
          {
            name: 'year',
            type: 'integer',
            required: false,
            description:
              'Reporting year for the resolved organization. If omitted, latest available year for that organization.',
          },
        ],
        headers: [authHeaderRow],
        curl: buildCurl(`${OCF_BASE_PATH}/location-summary`, [
          ['location_id', '55'],
          ['year', '2024'],
        ]),
        responses: [
          {
            label: 'Success (200) — with data',
            content: `{
  "organization_id": 102,
  "organization_name": "Acme Subsidiary",
  "location_id": 55,
  "location_name": "Plant A",
  "year": 2024,
  "data": {
    "revenue": {
      "revenue": 3000000.0,
      "currency": "CAD",
      "revenue_complete": true,
      "missing_revenue_locations": []
    },
    "emissions": {
      "total_co2e": 950.4321,
      "by_scope": {
        "scope_emission": [
          { "scope": "Scope 1", "total_co2e": 100.0, "percentage": 10.52 },
          { "scope": "Scope 2", "total_co2e": 250.4321, "percentage": 26.35 },
          { "scope": "Scope 3", "total_co2e": 600.0, "percentage": 63.13 }
        ]
      },
      "by_source": {
        "scope1_by_source": [
          { "label": "Natural gas", "total_co2e": 100.0, "percentage": 100.00 }
        ],
        "scope2_by_source": [
          { "label": "Electricity", "total_co2e": 250.4321, "percentage": 100.00 }
        ],
        "scope3_by_source": []
      }
    }
  }
}`,
          },
          {
            label: 'Success (200) — location_id omitted (selection helper)',
            content: `{
  "message": "Please Add location_id to the URL query to view data for a specific location. The available location names and IDs in your company are listed below.",
  "locations": [
    {
      "id": 55,
      "name": "Plant A",
      "location_type": "Processing facility"
    },
    {
      "id": 56,
      "name": "Warehouse B",
      "location_type": "Warehouse"
    }
  ]
}`,
          },
          {
            label: 'Success (200) — no data filled yet',
            content: `{
  "organization_id": 102,
  "organization_name": "Acme Subsidiary",
  "location_id": 55,
  "location_name": "Plant A",
  "year": 2024,
  "message": "Data for this location and year has not been filled yet.",
  "data": {}
}`,
          },
        ],
        fields: [
          {
            field: 'location_id / location_name',
            description: 'Set when location_id was resolved; otherwise null for org-level aggregate.',
          },
          {
            field: 'data.revenue.currency',
            description: 'ISO currency code for revenue (same key as organization-summary).',
          },
          {
            field: 'data.emissions.total_co2e',
            description: 'Sum of by_scope.scope_emission[].total_co2e after rounding rules.',
          },
          {
            field: 'data.emissions.by_source',
            description:
              'Same per-scope label / total_co2e semantics as organization-summary; zeros dropped and within-scope percentages recomputed.',
          },
          {
            field: 'message / locations',
            description:
              'Present when location_id is omitted: human-readable message and locations[] (id, name, location_type) for choosing a location_id.',
          },
        ],
        businessNotes: [
          'Selection helper: omit location_id to receive message + locations[] without a full data object.',
          'Location resolution: when location_id is provided, the location must exist, company_id must match, and it must be assigned to an organization.',
          'Year resolution: uses the location\'s organization (or parent) to resolve available years and default year.',
          'Empty state: if there is no revenue and no emission data, the API returns 200 with an empty data object and a message (no total_co2e).',
        ],
        errors: [
          {
            status: '400',
            situation: 'Invalid location_id / year when present',
            message: 'Must be positive integers',
          },
          {
            status: '404',
            situation: 'Location not found / wrong tenant',
            message: 'Location not found or does not belong to this account',
          },
          {
            status: '400',
            situation: 'Location without organization',
            message: 'Location is not assigned to an organization',
          },
          {
            status: '404',
            situation: 'Organization / year issues',
            message: 'Same as organization-summary as applicable',
          },
          { status: '400', situation: 'Unknown query parameters', message: 'Invalid query parameter list' },
          { status: '429', situation: 'Rate limited', message: 'Too many requests, please try again later' },
        ],
      },
      {
        id: 'ocf-list-locations',
        title: 'List Locations',
        navLabel: 'List Locations',
        method: 'GET',
        path: `${OCF_BASE_PATH}/list-locations`,
        description:
          'Returns every non-deleted location for the company with its current organization name (for display), type, and organization label. Duplicate location ids are suppressed.',
        queryParams: [],
        queryParamsNote: 'No query parameters are permitted. Any extra key returns 400 Bad Request.',
        headers: [authHeaderRow],
        curl: buildCurl(`${OCF_BASE_PATH}/list-locations`),
        responses: [
          {
            label: 'Success (200)',
            content: `{
  "locations": [
    {
      "id": 55,
      "name": "Location A",
      "location_type": "Processing facility",
      "location_in_organization": "Acme Subsidiary"
    },
    {
      "id": 56,
      "name": "Warehouse B",
      "location_type": "Warehouse",
      "location_in_organization": ""
    }
  ]
}`,
          },
        ],
        fields: [
          {
            field: 'locations[].id',
            description: `Location id (use with ${OCF_BASE_PATH}/location).`,
          },
          { field: 'locations[].name', description: 'Location name.' },
          {
            field: 'locations[].location_type',
            description: 'Classification string for the location (backend-defined values).',
          },
          {
            field: 'locations[].location_in_organization',
            description:
              'Name of the organization that owns the location; empty string if unknown or unassigned in data.',
          },
        ],
        businessNotes: [
          'Ordered by location id ascending.',
          'Only locations for the authenticated company_id are returned.',
        ],
        errors: [
          { status: '401', situation: 'Missing or invalid Authorization header', message: 'unauthorized' },
          { status: '403', situation: 'JWT used instead of API key', message: 'forbidden — JWT not accepted on /v1' },
          { status: '400', situation: 'Unknown query parameters', message: 'Invalid query parameter(s): ...' },
          { status: '429', situation: 'Rate limited', message: 'Too many requests, please try again later' },
        ],
      },
    ],
  },
];

export const globalErrorExamples: GlobalErrorExample[] = [
  {
    id: 'err-401',
    status: '401',
    title: '401 Unauthorized',
    description: 'Returned when the Authorization header is missing, malformed, or the API key is invalid or expired.',
    json: `{
  "error": "unauthorized",
  "error_code": null,
  "message": "Authorization header is required"
}`,
  },
  {
    id: 'err-403',
    status: '403',
    title: '403 Forbidden',
    description:
      'Returned when an API key lacks the required read scope.',
    json: `{
  "error": "unauthorized",
  "error_code": null,
  "message": "API keys are restricted to read-only (GET) requests"
}`,
  },
  {
    id: 'err-429',
    status: '429',
    title: '429 Too Many Requests',
    description:
      'Returned when either the per-IP failed-request limit (30/min) or the per-API-key success limit (100/min) is exceeded.',
    json: `{
  "error": {
    "message": "Too many requests, please try again later"
  }
}`,
  },
  {
    id: 'err-400',
    status: '400',
    title: '400 Bad Request',
    description:
      'Returned when an unknown query parameter is sent or a parameter fails validation (e.g. non-positive integer).',
    json: `{
  "error": "Invalid query parameter(s): year"
}`,
  },
  {
    id: 'err-404',
    status: '404',
    title: '404 Not Found',
    description:
      'Returned when a requested organization, location, or reporting year cannot be resolved for the authenticated company.',
    json: `{
  "error_code": "",
  "error": {
    "message": "Location not found or does not belong to this account",
    "details": ""
  }
}`,
  },
];

export const quickReferenceRows: QuickReferenceRow[] = [
  { endpoint: `GET ${OCF_BASE_PATH}/list-organizations`, allowed: '(none)' },
  { endpoint: `GET ${OCF_BASE_PATH}/organization-summary`, allowed: 'organization_id, year' },
  { endpoint: `GET ${OCF_BASE_PATH}/location`, allowed: 'location_id, year' },
  { endpoint: `GET ${OCF_BASE_PATH}/list-locations`, allowed: '(none)' },
];
